import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

/**
 * Configuración de beneficios por módulo para upselling
 */
const FEATURE_BENEFITS = {
    liveChat: {
        title: 'Toma el control de tus conversaciones',
        subtitle: 'No dejes que el bot haga todo. Intervén en tiempo real cuando sea necesario.',
        benefits: [
            { text: 'Intervención Humana', description: 'Pausa el bot y responde tú mismo al instante.' },
            { text: 'Audios y Multimedia', description: 'Envía notas de voz y archivos directamente.' },
            { text: 'Historial Completo', description: 'Accede a conversaciones pasadas sin límites.' },
            { text: 'Multi-Plataforma', description: 'Gestiona WhatsApp y Telegram desde un solo lugar.' }
        ],
        requiredPlan: 'basic',
        icon: '💬'
    },
    media_library: {
        title: 'Centraliza tus recursos de marketing',
        subtitle: 'Envía PDFs, imágenes y audios sin tener que subirlos cada vez.',
        benefits: [
            { text: 'Almacenamiento en la Nube', description: 'Tus archivos listos 24/7.' },
            { text: 'Respuestas Rápidas', description: 'Envía catálogos y precios en un clic.' },
            { text: 'Reutilización', description: 'Usa el mismo archivo en múltiples flujos.' },
            { text: 'Soporte Multimedia', description: 'Imágenes, Videos, Documentos y Audios.' }
        ],
        requiredPlan: 'pro',
        icon: '📚'
    },
    ai_analytics: {
        title: 'Entiende a tus clientes con IA',
        subtitle: 'Deja de adivinar. Deja que la IA te diga qué quieren tus usuarios.',
        benefits: [
            { text: 'Detección de Sentimientos', description: '¿Tus clientes están felices o enojados?' },
            { text: 'Resumen de Conversaciones', description: 'No leas todo, lee el resumen inteligente.' },
            { text: 'Identificación de Oportunidades', description: 'Detecta ventas potenciales automáticamente.' },
            { text: 'Optimización de Flujos', description: 'Descubre dónde se atascan tus usuarios.' }
        ],
        requiredPlan: 'pro',
        icon: '🧠'
    },
    intelligent_flows: {
        title: 'Captura Mensajes que tu Bot No Entiende',
        subtitle: 'Detecta automáticamente los "gaps" en tus flujos y conviértelos en nuevas reglas.',
        benefits: [
            { text: 'Detección Automática de Gaps', description: 'Captura mensajes que no coinciden con ninguna regla.' },
            { text: 'Métricas de Gaps', description: 'Ve cuántos mensajes únicos, de alta prioridad y totales recibes.' },
            { text: 'Agregar a Sub-regla con 1 Clic', description: 'Convierte un gap en una nueva regla al instante.' },
            { text: 'Análisis de Mensajes Frecuentes', description: 'Descubre qué preguntan más tus clientes.' }
        ],
        requiredPlan: 'pro',
        icon: '🧠'
    },
    ai_assistant: {
        title: 'Tu asistente de IA personal',
        subtitle: 'Deja que la IA responda por ti de forma inteligente y natural.',
        benefits: [
            { text: 'Respuestas Contextuales', description: 'La IA entiende el contexto de la conversación.' },
            { text: 'Aprendizaje Continuo', description: 'Mejora con cada interacción.' },
            { text: 'Personalización', description: 'Adapta el tono y estilo a tu marca.' },
            { text: 'Disponibilidad 24/7', description: 'Nunca pierdas una oportunidad de venta.' }
        ],
        requiredPlan: 'pro',
        icon: '🤖'
    }
};

const UpgradePrompt = ({ 
    feature, 
    requiredPlan: propRequiredPlan,
    title: propTitle,
    description: propDescription,
    benefits: propBenefits,
    fullPage = false 
}) => {
    const planNames = {
        basic: 'Basic',
        pro: 'Pro',
        business: 'Business',
        enterprise: 'Enterprise'
    };

    // Obtener configuración del módulo si existe
    const featureConfig = FEATURE_BENEFITS[feature] || {};
    
    // Usar props o valores del config o valores por defecto
    const requiredPlan = propRequiredPlan || featureConfig.requiredPlan || 'pro';
    const planName = planNames[requiredPlan] || 'Pro';
    
    const displayTitle = propTitle || featureConfig.title || `Funcionalidad ${feature || 'Premium'}`;
    const displaySubtitle = propDescription || featureConfig.subtitle || `Esta funcionalidad requiere el plan ${planName} o superior.`;
    const displayBenefits = propBenefits || featureConfig.benefits || [];
    const featureIcon = featureConfig.icon || '✨';

    // Colores según el plan
    const getColorClasses = (plan) => {
        switch(plan) {
            case 'basic':
                return {
                    bg: 'bg-blue-50 dark:bg-blue-900/20',
                    border: 'border-blue-200 dark:border-blue-800',
                    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
                    iconText: 'text-blue-600 dark:text-blue-400',
                    titleText: 'text-blue-900 dark:text-blue-300',
                    button: 'bg-blue-600 hover:bg-blue-700',
                    gradient: 'from-blue-500 to-blue-600'
                };
            case 'pro':
                return {
                    bg: 'bg-purple-50 dark:bg-purple-900/20',
                    border: 'border-purple-200 dark:border-purple-800',
                    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
                    iconText: 'text-purple-600 dark:text-purple-400',
                    titleText: 'text-purple-900 dark:text-purple-300',
                    button: 'bg-purple-600 hover:bg-purple-700',
                    gradient: 'from-purple-500 to-purple-600'
                };
            case 'business':
                return {
                    bg: 'bg-orange-50 dark:bg-orange-900/20',
                    border: 'border-orange-200 dark:border-orange-800',
                    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
                    iconText: 'text-orange-600 dark:text-orange-400',
                    titleText: 'text-orange-900 dark:text-orange-300',
                    button: 'bg-orange-600 hover:bg-orange-700',
                    gradient: 'from-orange-500 to-orange-600'
                };
            default:
                return {
                    bg: 'bg-purple-50 dark:bg-purple-900/20',
                    border: 'border-purple-200 dark:border-purple-800',
                    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
                    iconText: 'text-purple-600 dark:text-purple-400',
                    titleText: 'text-purple-900 dark:text-purple-300',
                    button: 'bg-purple-600 hover:bg-purple-700',
                    gradient: 'from-purple-500 to-purple-600'
                };
        }
    };

    const colors = getColorClasses(requiredPlan);

    if (fullPage) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-6">
                <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-100 dark:border-gray-700">
                    {/* Header con icono */}
                    <div className="text-center mb-8">
                        <div className={`mx-auto w-20 h-20 ${colors.iconBg} rounded-full flex items-center justify-center mb-4`}>
                            <span className="text-4xl">{featureIcon}</span>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                            {displayTitle}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 text-lg">
                            {displaySubtitle}
                        </p>
                    </div>

                    {/* Lista de beneficios */}
                    {displayBenefits.length > 0 && (
                        <div className={`${colors.bg} border ${colors.border} rounded-xl p-6 mb-6`}>
                            <div className="flex items-center gap-3 mb-4">
                                <Sparkles className={`w-5 h-5 ${colors.iconText}`} />
                                <h3 className={`text-lg font-bold ${colors.titleText}`}>
                                    ¿Qué obtienes con el Plan {planName}?
                                </h3>
                            </div>
                            <ul className="space-y-3">
                                {displayBenefits.map((benefit, index) => (
                                    <li key={`benefit-${index}`} className="flex items-start gap-3">
                                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <span className="font-semibold text-gray-800 dark:text-gray-200">
                                                {benefit.text}
                                            </span>
                                            {benefit.description && (
                                                <span className="text-gray-600 dark:text-gray-400">
                                                    {' — '}{benefit.description}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Badge del plan requerido */}
                    <div className="flex items-center justify-center gap-2 mb-6">
                        <Crown className={`w-5 h-5 ${colors.iconText}`} />
                        <span className={`font-medium ${colors.titleText}`}>
                            Disponible desde el Plan {planName}
                        </span>
                    </div>

                    {/* Botón CTA */}
                    <Link
                        to="/pricing"
                        className={`w-full py-4 px-6 bg-gradient-to-r ${colors.gradient} hover:opacity-90 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl`}
                    >
                        <Crown className="w-5 h-5" />
                        Ver Planes y Precios
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </div>
        );
    }

    // Versión compacta
    return (
        <div className={`${colors.bg} border ${colors.border} rounded-xl p-6`}>
            <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 ${colors.iconBg} rounded-lg flex items-center justify-center`}>
                    <span className="text-2xl">{featureIcon}</span>
                </div>
                <div className="flex-1">
                    <h3 className={`text-lg font-bold ${colors.titleText} mb-1`}>
                        {displayTitle}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-3">
                        {displaySubtitle}
                    </p>
                    
                    {/* Mini lista de beneficios en versión compacta */}
                    {displayBenefits.length > 0 && (
                        <ul className="space-y-1 mb-4">
                            {displayBenefits.slice(0, 3).map((benefit, index) => (
                                <li key={`compact-benefit-${index}`} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    <span className="font-medium">{benefit.text}</span>
                                </li>
                            ))}
                            {displayBenefits.length > 3 && (
                                <li className="text-sm text-gray-500 dark:text-gray-400 ml-6">
                                    + {displayBenefits.length - 3} beneficios más...
                                </li>
                            )}
                        </ul>
                    )}
                    
                    <Link
                        to="/pricing"
                        className={`inline-flex items-center gap-2 px-4 py-2 ${colors.button} text-white rounded-lg font-medium transition-all`}
                    >
                        <Crown className="w-4 h-4" />
                        Actualizar a {planName}
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default UpgradePrompt;
